import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ChevronLeft, ChevronRight, Award, Clock, MessageSquare, Search, TrendingUp, ExternalLink } from 'lucide-react';
import ApiService from '../../services/ApiService';
import DateRangePicker from '../ui/DatePicker';
import { LineChart } from '@mui/x-charts';
import dayjs from 'dayjs';

const CSATScores = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [csatData, setCsatData] = useState([]);
    const [csatPagination, setCsatPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [avgScore, setAvgScore] = useState(0);
    const [trendData, setTrendData] = useState({ dates: [], scores: [] });
    const [trendLoading, setTrendLoading] = useState(false);

    const fetchCsatScores = async (page = 1, since = startDate, until = endDate) => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                per_page: 10,
            };
            if (since && until) {
                params.start_date = dayjs(since).format('YYYY-MM-DD');
                params.end_date = dayjs(until).add(1, 'day').format('YYYY-MM-DD');
            }

            const response = await ApiService.getCSATScores(params);
            if (response.success) {
                setCsatData(response.data || []);
                setCsatPagination(response.pagination);

                // Calculate average for current set
                if (response.data?.length > 0) {
                    const sum = response.data.reduce((acc, curr) => acc + curr.score, 0);
                    setAvgScore((sum / response.data.length).toFixed(1));
                } else {
                    setAvgScore(0);
                }
            } else {
                setError(response.message || 'Failed to load CSAT scores');
            }
        } catch (err) {
            console.error('CSAT Fetch Error:', err);
            setError('An error occurred while fetching CSAT data');
        } finally {
            setLoading(false);
        }
    };

    const fetchTrendData = async (since = startDate, until = endDate) => {
        setTrendLoading(true);
        try {
            // Fetch a larger dataset for the trend, or use specific analytics endpoint if available
            // Here we reuse getCSATScores with a larger limit to approximate a trend
            // Ideally, the backend should provide an aggregation endpoint
            const params = {
                page: 1,
                per_page: 100, // Fetch more data for the trend
            };
            if (since && until) {
                params.start_date = dayjs(since).format('YYYY-MM-DD');
                params.end_date = dayjs(until).add(1, 'day').format('YYYY-MM-DD');
            }

            const response = await ApiService.getCSATScores(params);
            if (response.success && response.data) {
                // Group by date
                const grouped = response.data.reduce((acc, curr) => {
                    const date = dayjs(curr.created_at).format('YYYY-MM-DD');
                    if (!acc[date]) {
                        acc[date] = { sum: 0, count: 0 };
                    }
                    acc[date].sum += curr.score;
                    acc[date].count += 1;
                    return acc;
                }, {});

                // Sort by date and format for chart
                const sortedDates = Object.keys(grouped).sort();
                const scores = sortedDates.map(date =>
                    (grouped[date].sum / grouped[date].count).toFixed(1)
                );

                // Format dates for display (e.g., "Jan 1")
                const displayDates = sortedDates.map(date => dayjs(date).format('MMM D'));

                setTrendData({
                    dates: displayDates,
                    scores: scores.map(Number)
                });
            }
        } catch (err) {
            console.error('CSAT Trend Fetch Error:', err);
        } finally {
            setTrendLoading(false);
        }
    };

    useEffect(() => {
        fetchCsatScores();
        fetchTrendData();
    }, []);

    const handleDateRangeChange = (since, until) => {
        setStartDate(since);
        setEndDate(until);
        fetchCsatScores(1, since, until);
        fetchTrendData(since, until);
    };

    return (
        <div className="space-y-3 sm:space-y-4">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <div className="p-1.5 bg-black rounded-lg">
                                <Award className="h-5 w-5 text-white" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">
                                CSAT Scores
                            </h1>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500">
                            Analyze customer feedback and satisfaction ratings
                        </p>
                    </div>

                    <div className="z-20">
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleDateRangeChange}
                        />
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:border-black transition-all">
                        <div className="flex flex-col items-center text-center">
                            <div className="p-2 bg-black rounded-lg mb-2">
                                <Star className="h-5 w-5 text-white fill-white" />
                            </div>
                            <div className="text-2xl font-bold text-gray-900">{avgScore}</div>
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">Average Score</div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:border-black transition-all">
                        <div className="flex flex-col items-center text-center">
                            <div className="p-2 bg-black rounded-lg mb-2">
                                <MessageSquare className="h-5 w-5 text-white" />
                            </div>
                            <div className="text-2xl font-bold text-gray-900">{csatPagination?.total_items || 0}</div>
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">Total Responses</div>
                        </div>
                    </div>

                    {/* Trend Chart */}
                    <div className="sm:col-span-2 bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm hover:border-black transition-all flex flex-col">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="h-4 w-4 text-black" />
                            <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                                CSAT Trend {startDate && endDate ? `(${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('MMM D')})` : '(Recent)'}
                            </span>
                        </div>
                        <div className="flex-1 w-full h-40">
                            {trendLoading ? (
                                <div className="h-full flex items-center justify-center">
                                    <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : trendData.scores.length > 0 ? (
                                <LineChart
                                    xAxis={[{
                                        data: trendData.dates,
                                        scaleType: 'point',
                                        tickLabelStyle: {
                                            fontSize: 10,
                                            angle: 0,
                                        }
                                    }]}
                                    series={[
                                        {
                                            data: trendData.scores,
                                            color: '#000000',
                                            area: true,
                                            showMark: true,
                                            curve: "catmullRom",
                                        },
                                    ]}
                                    height={160}
                                    margin={{ top: 10, bottom: 20, left: 30, right: 10 }}
                                    sx={{
                                        '.MuiLineElement-root': {
                                            strokeWidth: 3,
                                        },
                                        '.MuiAreaElement-root': {
                                            fill: 'url(#gradient)',
                                            opacity: 0.2,
                                        },
                                    }}
                                >
                                    <defs>
                                        <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#000000" stopOpacity={0.5} />
                                            <stop offset="100%" stopColor="#000000" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                </LineChart>
                            ) : (
                                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                                    No data available for trend analysis
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
                        <h3 className="font-bold text-gray-900 text-sm">
                            Recent Submissions
                        </h3>
                        {loading && <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Rating</th>
                                    <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Customer Comment</th>
                                    <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Query ID</th>
                                    <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Submitted On</th>
                                    <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {error ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-red-600">
                                            <p className="mb-4 font-medium">{error}</p>
                                            <button
                                                onClick={() => fetchCsatScores()}
                                                className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                                            >
                                                Retry Loading
                                            </button>
                                        </td>
                                    </tr>
                                ) : csatData.length > 0 ? (
                                    csatData.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0">
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="flex">
                                                        {[1, 2, 3, 4, 5].map((s) => (
                                                            <Star
                                                                key={s}
                                                                className={`w-4 h-4 ${s <= item.score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-900">{item.score}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3">
                                                <p className="text-sm text-gray-600 max-w-md italic leading-snug">
                                                    {item.comment ? `"${item.comment}"` : <span className="text-gray-400 font-normal">No feedback provided</span>}
                                                </p>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap">
                                                <span className="text-[10px] text-gray-400 font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                                                    {item.query_id}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center gap-1.5 text-xs">
                                                    <Clock className="w-3 h-3 text-gray-400" />
                                                    {dayjs(item.created_at).format('MMM D, YYYY')}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                <Link
                                                    to={`/support-inbox?search_query=${item.query_id}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-white bg-black hover:bg-gray-800 rounded-lg transition-colors group"
                                                >
                                                    <span className="text-xs font-bold">View</span>
                                                    <ExternalLink className="h-3 w-3" />
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                ) : !loading && (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center">
                                                <div className="p-4 bg-gray-100 rounded-full mb-4">
                                                    <Search className="h-8 w-8 text-gray-400" />
                                                </div>
                                                <h4 className="text-lg font-bold text-gray-900">No CSAT Scores Found</h4>
                                                <p className="text-sm text-gray-500 max-w-xs mx-auto mt-1">
                                                    Ratings will appear here once customers start completing surveys.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {csatPagination && csatPagination.total_pages > 1 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                Showing <span className="font-medium">{(csatPagination.current_page - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(csatPagination.current_page * 10, csatPagination.total_items)}</span> of <span className="font-medium">{csatPagination.total_items}</span> results
                            </p>
                            <div className="flex gap-2">
                                <button
                                    disabled={!csatPagination.has_previous}
                                    onClick={() => fetchCsatScores(csatPagination.current_page - 1)}
                                    className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                                </button>
                                <button
                                    disabled={!csatPagination.has_next}
                                    onClick={() => fetchCsatScores(csatPagination.current_page + 1)}
                                    className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="h-5 w-5 text-gray-600" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
        </div>
    );
};

export default CSATScores;
